import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Spinner
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import api from '../helpers/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ToolCheckoutModal = ({ isOpen, toggle, toolId: prefilledToolId, workOrderId: prefilledWorkOrderId, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingWOs, setLoadingWOs] = useState(false);
  const [activeWorkOrders, setActiveWorkOrders] = useState([]);
  const [error, setError] = useState('');

  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [expectedReturnDate, setExpectedReturnDate] = useState(null);
  const [conditionOptions, setConditionOptions] = useState([]);

  const fetchConditions = async () => {
    try {
      const { data } = await api.get('dynamic-enum/find-by-name?name=condition');
      const values = data?.response?.values || [];
      const options = values.map(c => ({
        value: c,
        label: t(`shared.condition_${c.toLowerCase()}`, c)
      }));
      setConditionOptions(options);
      // Default to 'Good' if available
      const defaultOpt = options.find(o => o.value === 'Good') || options[0];
      setSelectedCondition(defaultOpt || null);
    } catch (err) {
      console.error('Error fetching conditions:', err);
      // Fallback to hardcoded conditions
      const fallback = [
        { value: 'New', label: t('shared.condition_new', 'New') },
        { value: 'Good', label: t('shared.condition_good', 'Good') },
        { value: 'Fair', label: t('shared.condition_fair', 'Fair') },
        { value: 'Poor', label: t('shared.condition_poor', 'Poor') }
      ];
      setConditionOptions(fallback);
      setSelectedCondition(fallback[1]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (!prefilledWorkOrderId) {
        fetchActiveWorkOrders();
      }
      fetchConditions();
      setError('');
      setSelectedWorkOrder(null);
      setExpectedReturnDate(null);

      if (prefilledToolId) {
        fetchToolById(prefilledToolId);
      } else {
        setSelectedTool(null);
      }
    }
  }, [isOpen, prefilledWorkOrderId, prefilledToolId]);

  const fetchToolById = async (id) => {
    try {
      const { data } = await api.get(`tool/get-by-id?id=${id}`);
      const tool = data?.response;
      if (tool) {
        const toolOption = { value: tool.id, label: tool.name, serialNumber: tool.serialNumber, location: tool.location, condition: tool.condition };
        setSelectedTool(toolOption);
        autoSetCondition(toolOption);
      }
    } catch (err) {
      console.error('Error fetching tool:', err);
      setSelectedTool(null);
    }
  };

  const fetchActiveWorkOrders = async () => {
    try {
      setLoadingWOs(true);
      const { data } = await api.get('asset-issues/get-by-status?status=IN_PROGRESS&count=100');
      setActiveWorkOrders(data?.response || []);
    } catch (err) {
      console.error('Error fetching active work orders:', err);
      setActiveWorkOrders([]);
    } finally {
      setLoadingWOs(false);
    }
  };

  const loadToolOptions = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      const { data } = await api.get(`tool/all?status=AVAILABLE&search=${encodeURIComponent(inputValue)}&count=20`);
      return (data?.response || []).map(tool => ({
        value: tool.id,
        label: tool.name,
        serialNumber: tool.serialNumber,
        location: tool.location,
        condition: tool.condition
      }));
    } catch (err) {
      console.error('Error searching tools:', err);
      return [];
    }
  }, []);

  const autoSetCondition = (toolOption) => {
    if (toolOption?.condition) {
      const match = conditionOptions.find(c => c.value === toolOption.condition.toUpperCase());
      if (match) setSelectedCondition(match);
    }
  };

  const handleToolChange = (option) => {
    setSelectedTool(option);
    if (option) {
      autoSetCondition(option);
    } else {
      setSelectedCondition(conditionOptions[1]); // Reset to GOOD
    }
  };

  const formatToolOptionLabel = (option) => (
    <div>
      <div>{option.label}</div>
      {(option.serialNumber || option.location) && (
        <small className="text-muted">
          {option.serialNumber || ''}{option.serialNumber && option.location ? ' - ' : ''}{option.location || ''}
        </small>
      )}
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedTool) {
      setError(t('shared.tool_name_required', 'Tool name is required'));
      return;
    }

    try {
      setLoading(true);
      const payload = {
        toolId: selectedTool.value,
        conditionAtCheckout: selectedCondition?.value || 'GOOD'
      };
      if (prefilledWorkOrderId) {
        payload.workOrderId = parseInt(prefilledWorkOrderId);
      } else if (selectedWorkOrder) {
        payload.workOrderId = parseInt(selectedWorkOrder.value);
      }
      if (expectedReturnDate) {
        // Backend expects LocalDateTime format
        const d = new Date(expectedReturnDate);
        payload.expectedReturnDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T00:00:00`;
      }

      await api.post('work-order-tool/checkout', payload);

      if (onSuccess) onSuccess();
      toggle();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || t('shared.something_went_wrong', 'Something went wrong!');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const workOrderOptions = activeWorkOrders.map(wo => ({
    value: wo.id,
    label: `${wo.ticketNumber ? `${wo.ticketNumber} - ` : ''}${wo.eventTitle || wo.title || `WO #${wo.id}`}`
  }));

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md" centered backdrop="static">
      <div className="modal-header">
        <h5 className="modal-title">{t('business.tools.check_out', 'Check Out')}</h5>
        <button type="button" className="btn-close" onClick={toggle} aria-label={t('shared.close', 'Close')} />
      </div>

      <div className="modal-body">
        <form onSubmit={handleSubmit}>
          {/* Tool Selection */}
          <div className="mb-4">
            <label className="form-label fw-medium">
              {t('shared.tool_name', 'Tool Name')} <span className="text-danger">*</span>
            </label>
            <AsyncSelect
              cacheOptions
              loadOptions={loadToolOptions}
              value={selectedTool}
              onChange={handleToolChange}
              formatOptionLabel={formatToolOptionLabel}
              placeholder={t('business.tools.search_available_tool', 'Search available tools to check out...')}
              noOptionsMessage={({ inputValue }) =>
                inputValue.length < 2
                  ? t('business.tools.type_to_search', 'Type to search...')
                  : t('business.tools.no_available_tools', 'No available tools found')
              }
              isClearable={!prefilledToolId}
              isDisabled={!!prefilledToolId}
              classNamePrefix="react-select"
            />
          </div>

          {/* Work Order Selection - only shown when NOT pre-filled */}
          {!prefilledWorkOrderId && (
            <div className="mb-4">
              <label className="form-label fw-medium">
                {t('shared.work_order', 'Work Order')}
                <small className="text-muted ms-1">{t('business.tools.optional', '(Optional)')}</small>
              </label>
              {loadingWOs ? (
                <div className="d-flex align-items-center py-2">
                  <Spinner size="sm" color="primary" className="me-2" />
                  <span className="text-muted small">{t('shared.loading', 'Loading...')}</span>
                </div>
              ) : (
                <Select
                  options={workOrderOptions}
                  value={selectedWorkOrder}
                  onChange={setSelectedWorkOrder}
                  isClearable
                  isSearchable
                  placeholder={t('business.tools.select_work_order', 'Select Work Order (optional)')}
                  classNamePrefix="react-select"
                />
              )}
            </div>
          )}

          {/* Expected Return Date */}
          <div className="mb-4">
            <label className="form-label fw-medium d-block">
              {t('business.tools.expected_return_date', 'Expected Return Date')}
            </label>
            <div className="w-100">
              <DatePicker
                selected={expectedReturnDate}
                onChange={setExpectedReturnDate}
                dateFormat="dd/MM/yyyy"
                className="form-control w-100"
                wrapperClassName="w-100"
                placeholderText={t('shared.select_date', 'Select date')}
                isClearable
                minDate={new Date()}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>
          </div>

          {/* Condition at Checkout */}
          <div className="mb-3">
            <label className="form-label fw-medium">
              {t('business.tools.condition_at_checkout', 'Condition at Checkout')}
            </label>
            <Select
              options={conditionOptions}
              value={selectedCondition}
              onChange={setSelectedCondition}
              classNamePrefix="react-select"
              isSearchable={false}
            />
          </div>

          {error && <div className="alert alert-danger mt-3">{error}</div>}

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-secondary" onClick={toggle} disabled={loading}>
              {t('shared.cancel', 'Cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading || !selectedTool}>
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t('shared.processing', 'Processing...')}
                </>
              ) : (
                <>
                  <i className="bx bx-log-out me-1"></i>
                  {t('business.tools.check_out', 'Check Out')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ToolCheckoutModal;
